// getter/setter for EditProductForm
import { atom } from "jotai";
import { atomWithReset } from "jotai/utils";
import {
  MINIMUM_PRICE_PER_ALBUM,
  MINIMUM_PRICE_PER_RELEASE_TRACK,
  MINIMUM_UNIT_PRICE,
} from "../../constants/releases";
import { DEFAULT_RELEASE_TITLE } from "../../store/models/release";

export const DEFAULT_RELEASE_PRICE = 9.99;
export const DEFAULT_RELEASE_TRACK_PRICE = 1.0;

export interface EditProductFormState {
  downloadOptions: string[];
  albumPrice: string;
  trackPrice: string;
  publishedAt: Date | null;
  publishedEnd: Date | null;
  payWhatYouWant: boolean;
  minPayWhatYouWantPrice?: string;
  name?: string;
  coverPhotoId?: number | null;
  description?: string;
}

export const initialState: EditProductFormState = {
  downloadOptions: [],
  albumPrice: DEFAULT_RELEASE_PRICE.toString(),
  trackPrice: DEFAULT_RELEASE_TRACK_PRICE.toString(),
  publishedAt: null,
  publishedEnd: null,
  payWhatYouWant: false,
  name: DEFAULT_RELEASE_TITLE,
  coverPhotoId: null,
  description: "",
};

export const editProductFormStateAtom = atomWithReset(initialState);

export const editProductFormAtom = atom(
  (get) => get(editProductFormStateAtom),
  (get, set, updates: Partial<EditProductFormState>) =>
    set(editProductFormStateAtom, {
      ...get(editProductFormStateAtom),
      ...updates,
    }),
);

export const isPublishedEndValidAtom = atom((get) => {
  const formState = get(editProductFormStateAtom);
  if (!formState.publishedEnd) return true;
  return Boolean(
    formState.publishedEnd &&
      formState.publishedAt &&
      formState.publishedEnd > formState.publishedAt,
  );
});

export const isIndividualTrackSaleEnabledAtom = atom(false);

export const isTrackPriceValidAtom = atom((get) => {
  const isIndividualTrackSaleEnabled = get(isIndividualTrackSaleEnabledAtom);
  const trackPrice = parseFloat(get(editProductFormStateAtom).trackPrice);
  // parseFloat for null or undefined will return NaN. We want to consider form
  // valid in this case (when everything is cleared)
  if (Number.isNaN(trackPrice) || !isIndividualTrackSaleEnabled) return true;
  return trackPrice >= MINIMUM_UNIT_PRICE;
});

const _isAlbumPriceValidAtom = atom(true);
export const isAlbumPriceValidAtom = atom(
  (get) => get(_isAlbumPriceValidAtom),
  (get, set, trackCount: number | null | undefined) => {
    const albumPrice = parseFloat(get(editProductFormAtom).albumPrice);

    // parseFloat for null or undefined will return NaN. We want to consider form
    // valid in this case (when everything is cleared)
    if (Number.isNaN(albumPrice)) {
      set(_isAlbumPriceValidAtom, true);
      return true;
    }

    if (!trackCount) return true;
    const dynamicMinimumAlbumPrice =
      MINIMUM_PRICE_PER_RELEASE_TRACK * trackCount;
    const validatedMinimumAlbumPrice = Math.max(
      dynamicMinimumAlbumPrice,
      MINIMUM_PRICE_PER_ALBUM,
    );
    set(_isAlbumPriceValidAtom, albumPrice >= validatedMinimumAlbumPrice);
  },
);

export const isEditProductFormValidAtom = atom((get) => {
  const formState = get(editProductFormStateAtom);
  const publishedEndIsValid = formState.publishedEnd
    ? get(isPublishedEndValidAtom)
    : true;
  const isPayWhatYouWantValid = formState.payWhatYouWant
    ? Boolean(formState.minPayWhatYouWantPrice)
    : true;
  const isTrackPriceValid = get(isTrackPriceValidAtom);
  const isAlbumPriceValid = get(isAlbumPriceValidAtom);
  return Boolean(
    formState.albumPrice &&
      formState.publishedAt &&
      publishedEndIsValid &&
      isPayWhatYouWantValid &&
      isTrackPriceValid &&
      isAlbumPriceValid,
  );
});

editProductFormAtom.debugLabel = "Edit Product Form Atom";
